import { Component, Suspense, lazy } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import type { CoverageMapProps } from './types';

const loadCoverageMap = () => import('./CoverageMap');

const LazyCoverageMap = lazy(loadCoverageMap);

export function preloadCoverageMap(): Promise<unknown> {
  return loadCoverageMap();
}

interface BoundaryProps {
  fallback: ReactNode;
  onError?: (error: Error, info: ErrorInfo) => void;
  children: ReactNode;
}

interface BoundaryState {
  failed: boolean;
}

class CoverageMapBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info);
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export interface CoverageMapLazyProps extends CoverageMapProps {
  fallback?: ReactNode;
  errorFallback?: ReactNode;
  onLoadError?: (error: Error, info: ErrorInfo) => void;
}

export function CoverageMapLazy({
  fallback = null,
  errorFallback = null,
  onLoadError,
  ...props
}: CoverageMapLazyProps) {
  return (
    <CoverageMapBoundary fallback={errorFallback} onError={onLoadError}>
      <Suspense fallback={fallback}>
        <LazyCoverageMap {...props} />
      </Suspense>
    </CoverageMapBoundary>
  );
}
